// src/lib/email/emailService.ts

/**
 * Email service using PHP endpoint on helpkeepmymoney.com server
 * Sends notification emails to helpkeepmymoney@gmail.com via Resend.com API
 */

const EMAIL_API_URL = 'https://helpkeepmymoney.com/send-email.php';

/**
 * Sends notification email when a new client signs up
 */
export async function sendNewClientSignupNotification(clientEmail: string, userId: string): Promise<void> {
  try {
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'signup',
        clientEmail,
        userId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Email API error: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('Signup notification sent successfully:', result);
  } catch (error: any) {
    console.error('Error sending signup notification:', {
      error: error.message || error,
    });
    // Don't throw - we don't want email failures to break the main flow
  }
}

/**
 * Sends notification email when an entity formation form is submitted
 */
export async function sendEntityFormationSubmissionNotification(
  clientEmail: string,
  clientName: string,
  businessName: string,
  submissionId: string
): Promise<void> {
  try {
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'entityFormation',
        clientEmail,
        clientName,
        businessName,
        submissionId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Email API error: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('Entity formation notification sent successfully:', result);
  } catch (error: any) {
    console.error('Error sending entity formation notification:', {
      error: error.message || error,
    });
    // Don't throw - we don't want email failures to break the main flow
  }
}

/**
 * Sends notification email when a client creates or updates the onboarding form
 */
export async function sendOnboardingFormNotification(
  clientEmail: string,
  clientName: string,
  businessName: string,
  businessType: string,
  ein: string,
  selectedServices: string[],
  isUpdate: boolean
): Promise<void> {
  console.log('sendOnboardingFormNotification called with:', {
    clientEmail,
    clientName,
    businessName,
    businessType,
    ein,
    selectedServices,
    isUpdate,
    isUpdateType: typeof isUpdate,
  });

  try {
    const requestBody = {
      type: 'onboarding',
      clientEmail,
      clientName,
      businessName,
      businessType,
      ein,
      selectedServices,
      isUpdate: Boolean(isUpdate), // Ensure it's a boolean
    };

    console.log('Sending email request:', requestBody);

    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('Email API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('Email API error response:', errorData);
      throw new Error(`Email API error: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('Onboarding form notification sent successfully:', result);
  } catch (error: any) {
    console.error('Error sending onboarding form notification:', {
      error: error.message || error,
      stack: error.stack,
    });
    // Don't throw - we don't want email failures to break the main flow
  }
}

/**
 * Sends notification emails when a privacy request status changes
 */
export async function sendPrivacyRequestStatusNotification(
  requestType: string,
  status: string,
  clientEmail: string,
  requestId: string,
  requestDescription?: string,
  notes?: string
): Promise<void> {
  try {
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'privacyRequestStatus',
        requestType,
        status,
        clientEmail,
        requestId,
        requestDescription,
        notes,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Email API error: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('Privacy request status notification sent successfully:', result);
  } catch (error: any) {
    console.error('Error sending privacy request status notification:', {
      error: error.message || error,
    });
    // Don't throw - we don't want email failures to break the main flow
  }
}

/**
 * Sends notification when a new privacy request is submitted
 */
export async function sendNewPrivacyRequestNotification(
  requestType: string,
  clientEmail: string,
  requestId: string,
  requestDescription?: string
): Promise<void> {
  try {
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'newPrivacyRequest',
        requestType,
        clientEmail,
        requestId,
        requestDescription: requestDescription || '', // Ensure it's always a string, not undefined
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Email API error: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('New privacy request notification sent successfully:', result);
  } catch (error: any) {
    console.error('Error sending new privacy request notification:', {
      error: error.message || error,
    });
    // Don't throw - we don't want email failures to break the main flow
  }
}

/**
 * Sends deadline reminder emails for privacy requests
 */
export async function sendPrivacyRequestDeadlineReminder(
  requestType: string,
  clientEmail: string,
  requestId: string,
  daysRemaining: number,
  deadlineDate: string
): Promise<void> {
  try {
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'privacyRequestDeadline',
        requestType,
        clientEmail,
        requestId,
        daysRemaining,
        deadlineDate,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Email API error: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('Privacy request deadline reminder sent successfully:', result);
  } catch (error: any) {
    console.error('Error sending privacy request deadline reminder:', {
      error: error.message || error,
    });
    // Don't throw - we don't want email failures to break the main flow
  }
}

/**
 * Sends GLBA Financial Privacy Notice email
 */
export async function sendGLBAPrivacyNotice(
  clientEmail: string,
  noticeType: 'initial' | 'annual'
): Promise<void> {
  try {
    const response = await fetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'glbaPrivacyNotice',
        clientEmail,
        noticeType,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(`Email API error: ${errorData.error || response.statusText}`);
    }

    const result = await response.json();
    console.log('GLBA privacy notice sent successfully:', result);
  } catch (error: any) {
    console.error('Error sending GLBA privacy notice:', {
      error: error.message || error,
    });
    // Don't throw - we don't want email failures to break the main flow
  }
}
